import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getIncidents } from '../../shared/api/api';
import { background } from '../../shared/api/axiosConfig';
import LoadingSpinner from '../../shared/components/LoadingSpinner';
import Pagination from '../../shared/components/Pagination';
import FilterDropdown from '../../shared/components/FilterDropdown';
import Toast from '../../shared/components/Toast';
import usePagination from '../../shared/hooks/usePagination';
import useUpdateEffect from '../../shared/hooks/useUpdateEffect';
import useFlashMessage from '../../shared/hooks/useFlashMessage';
import useResizableColumns, { ICON_COL_MIN_WIDTH } from '../../shared/hooks/useResizableColumns';
import '../../shared/styles/resizableTable.css';
import './IncidentsPage.css';

const INCIDENTS_COLUMNS = [
  { key: 'reference_number', label: 'Ref', width: 160 },
  { key: 'severity', label: 'Severity', width: 100 },
  { key: 'status', label: 'Status', width: 110 },
  { key: 'title', label: 'Title', width: 220 },
  { key: 'service', label: 'Service', width: 140 },
  { key: 'assignee', label: 'Assignee', width: 130 },
  { key: 'created_at', label: 'Created', width: 150 },
  { key: 'escalation', label: 'Escalation', width: 120 },
  { key: 'action', label: '', width: 90, minWidth: ICON_COL_MIN_WIDTH },
];

function IncidentsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [incidents, setIncidents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [lastUpdated, setLastUpdated] = useState(null);

  // A one-off confirmation from an action taken on another page — cancelling
  // an incident redirects here, and shouldn't leave the user guessing
  // whether it worked.
  const [flashMessage, dismissFlash] = useFlashMessage();

  // Restored from navigation state when returning via "Back to Incidents" —
  // see the View button below and IncidentDetailPage's back button, which
  // relays this whole object rather than picking fields out of it.
  const restore = location.state || {};
  const [severityFilter, setSeverityFilter] = useState(restore.severityFilter || []);
  const [statusFilter, setStatusFilter] = useState(restore.statusFilter || []);
  const [serviceFilter, setServiceFilter] = useState(restore.serviceFilter || []);
  const [searchQuery, setSearchQuery] = useState(restore.searchQuery || '');
  const [sortField, setSortField] = useState(restore.sortField || 'created_at');
  const [sortDirection, setSortDirection] = useState(restore.sortDirection || 'desc');

  const { colWidths, tableWidth, handleResizeStart } = useResizableColumns(INCIDENTS_COLUMNS);

  useEffect(() => {
    fetchIncidents();
  }, []);

  /**
   * @param isBackground true for the 30-second auto-refresh, false when the
   *        person asked for it (first load, or the Refresh button).
   *
   * A background pass deliberately does not touch `loading` or `error`. It used
   * to set both, so every thirty seconds the full-screen spinner flashed over
   * the page and a momentary failure replaced a perfectly good list with an
   * error. That is what the page "refreshing itself" was.
   */
  const fetchIncidents = useCallback(async (isBackground = false) => {
    if (!isBackground) setLoading(true);
    try {
      const response = await getIncidents(isBackground ? background() : undefined);
      const data = response.data;
      const list = Array.isArray(data) ? data : data?.incidents || data?.content || [];
      setIncidents(list);
      setLastUpdated(new Date());
      if (!isBackground) setError('');
    } catch {
      // A failed background pass leaves the last good list on screen. The
      // "Updated ..." time stops advancing, which is the honest signal that
      // what you are looking at is ageing.
      if (!isBackground) setError('Failed to load incidents.');
    } finally {
      if (!isBackground) setLoading(false);
    }
  }, []);

  /**
   * Whether to skip this auto-refresh pass.
   *
   * Refreshing the table under someone who is typing a search or choosing a
   * filter moves the thing they are reaching for. Waiting 30 seconds costs
   * nothing; the next pass picks it up.
   */
  const isMidTask = () => {
    if (document.hidden) return true; // nobody is looking at it
    const tag = document.activeElement?.tagName?.toLowerCase();
    return tag === 'input' || tag === 'textarea' || tag === 'select';
  };

  // Auto-refresh every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      if (!isMidTask()) fetchIncidents(true);
    }, 30000);
    return () => clearInterval(interval);
  }, [fetchIncidents]);

  const severityOptions = useMemo(
    () => [...new Set(incidents.map((i) => i.severity).filter(Boolean))].sort(),
    [incidents]
  );

  const statusOptions = useMemo(
    () => [...new Set(incidents.map((i) => i.status).filter(Boolean))].sort(),
    [incidents]
  );

  const serviceOptions = useMemo(
    () => [...new Set(incidents.map((i) => i.service).filter(Boolean))].sort(),
    [incidents]
  );

  const hasActiveFilters =
    severityFilter.length > 0 ||
    statusFilter.length > 0 ||
    serviceFilter.length > 0 ||
    searchQuery.trim() !== '';

  const clearAllFilters = useCallback(() => {
    setSeverityFilter([]);
    setStatusFilter([]);
    setServiceFilter([]);
    setSearchQuery('');
  }, []);

  const getSeverityClass = (severity) => {
    switch (severity?.toUpperCase()) {
      case 'CRITICAL': return 'critical';
      case 'HIGH': return 'high';
      case 'MEDIUM': return 'medium';
      case 'LOW': return 'low';
      default: return 'info';
    }
  };

  const getStatusClass = (status) => {
    switch (status?.toUpperCase()) {
      case 'NEW': return 'new';
      case 'IN_PROGRESS': return 'in-progress';
      case 'PENDING_VERIFY': return 'pending-verify';
      case 'RESOLVED': return 'resolved';
      case 'CLOSED': return 'closed';
      default: return 'unknown';
    }
  };

  const filteredIncidents = incidents
    .filter((i) => severityFilter.length === 0 || severityFilter.includes(i.severity))
    .filter((i) => statusFilter.length === 0 || statusFilter.includes(i.status))
    .filter((i) => serviceFilter.length === 0 || serviceFilter.includes(i.service))
    .filter((i) => {
      if (!searchQuery.trim()) return true;
      const q = searchQuery.toLowerCase();
      return (
        i.title?.toLowerCase().includes(q) ||
        i.reference_number?.toLowerCase().includes(q) ||
        i.alarm_name?.toLowerCase().includes(q) ||
        i.description?.toLowerCase().includes(q) ||
        i.service?.toLowerCase().includes(q) ||
        i.incident_id?.toLowerCase().includes(q) ||
        i.assignee?.toLowerCase().includes(q)
      );
    })
    .sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];
      if (['created_at', 'updated_at', 'resolved_at', 'sla_deadline'].includes(sortField)) {
        aVal = aVal ? new Date(aVal).getTime() : 0;
        bVal = bVal ? new Date(bVal).getTime() : 0;
      } else {
        aVal = (aVal || '').toString().toLowerCase();
        bVal = (bVal || '').toString().toLowerCase();
      }
      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

  const {
    currentPage,
    pageSize,
    totalPages,
    paginated,
    setCurrentPage,
    setPageSize,
    resetPage,
  } = usePagination(filteredIncidents, 15, restore.page);

  // useUpdateEffect (not useEffect) so this doesn't fire on mount — that would
  // immediately overwrite the page restored above, from returning via "Back
  // to Incidents" to something other than page 1.
  useUpdateEffect(() => {
    resetPage();
  }, [severityFilter, statusFilter, serviceFilter, searchQuery]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortIndicator = (field) => {
    const icon = sortField === field ? (sortDirection === 'asc' ? '\u25B2' : '\u25BC') : '';
    return <span className="incidents-sort-icon">{icon}</span>;
  };

  const formatTimestamp = (ts) => {
    if (!ts) return '\u2014';
    return new Date(ts).toLocaleString();
  };

  // The single definition of "this incident is finished".
  //
  // RESOLVED and CLOSED are the complete set of terminal statuses, so asking
  // "has it finished?" stays correct as new active statuses appear. The Open
  // tile used to ask the opposite question — "is it one of NEW, IN_PROGRESS or
  // PENDING_VERIFY?" — and that list was never complete: ESCALATED is a real
  // status on this page (see getEscalationLabel below) and was counted as
  // neither open nor resolved, so escalating an incident removed it from the
  // tile entirely. That was TSB-4223.
  //
  // Uppercasing matches getStatusClass/getSeverityClass, which already do it.
  // Without it a lowercase status renders a correct badge in the table while
  // being undercounted in the tile — same symptom, different cause.
  //
  // slaBreachedCount always had this logic right, eleven lines below the tile
  // that had it wrong. Both now share this helper: a second copy is how the two
  // drifted apart in the first place, and how a future status could break one
  // while the other keeps working.
  const isFinished = (status) => ['RESOLVED', 'CLOSED'].includes((status || '').toUpperCase());

  // Stats
  const openCount = incidents.filter((i) => !isFinished(i.status)).length;
  const criticalCount = incidents.filter((i) => (i.severity || '').toUpperCase() === 'CRITICAL').length;
  const resolvedCount = incidents.filter((i) => (i.status || '').toUpperCase() === 'RESOLVED').length;

  // CLOSED needs its own tile because it is neither of the two above: Open
  // excludes it for being finished, Resolved excludes it for not being that
  // exact status. With only three tiles a closed incident appeared in none of
  // them, so Open + Resolved fell short of Total by however many were closed --
  // reported from sitops as "the Open box is missing 1". Nothing was miscounted;
  // there was simply nowhere for it to show.
  //
  // The two terminal statuses stay on separate tiles rather than being merged
  // into one "finished" count: resolved and closed mean different things to the
  // people reading this page, and collapsing them would trade one confusing
  // display for another. Open + Resolved + Closed now equals Total exactly.
  const closedCount = incidents.filter((i) => (i.status || '').toUpperCase() === 'CLOSED').length;

  // SLA breach — active incidents past their SLA deadline
  const slaBreachedCount = incidents.filter((i) => {
    if (!i.sla_deadline) return false;
    if (isFinished(i.status)) return false;
    return new Date(i.sla_deadline) < new Date();
  }).length;

  const getEscalationLabel = (status) => {
    if (status === 'ESCALATED') return 'ESCALATED';
    return null;
  };

  return (
    <div className="page-container">
      {loading && <LoadingSpinner message="Loading incidents..." />}
      <Toast message={flashMessage} onDismiss={dismissFlash} />
      <div className="incidents-header">
        <h1 className="page-title">Incidents</h1>
        <div className="incidents-stats">
          <div className="incidents-stat">
            <span className="incidents-stat-value">{incidents.length}</span>
            <span className="incidents-stat-label">Total</span>
          </div>
          <div className="incidents-stat incidents-stat--open">
            <span className="incidents-stat-value">{openCount}</span>
            <span className="incidents-stat-label">Open</span>
          </div>
          <div className="incidents-stat incidents-stat--critical">
            <span className="incidents-stat-value">{criticalCount}</span>
            <span className="incidents-stat-label">Critical</span>
          </div>
          <div className="incidents-stat incidents-stat--resolved">
            <span className="incidents-stat-value">{resolvedCount}</span>
            <span className="incidents-stat-label">Resolved</span>
          </div>
          <div className="incidents-stat incidents-stat--closed">
            <span className="incidents-stat-value">{closedCount}</span>
            <span className="incidents-stat-label">Closed</span>
          </div>
        </div>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {/* SLA violation banner */}
      {slaBreachedCount > 0 && (
        <div className="incidents-sla-banner">
          <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
          <span>{slaBreachedCount} incident{slaBreachedCount > 1 ? 's have' : ' has'} breached SLA and require immediate attention.</span>
        </div>
      )}

      {/* Filter bar */}
      <div className="incidents-filter-bar">
        <div className="incidents-filter-row">
          <div className="incidents-search-field">
            <svg className="incidents-search-icon" viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>
            <input
              type="text"
              className="incidents-search-input"
              placeholder="Search incidents..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button className="incidents-search-clear" onClick={() => setSearchQuery('')}>{'\u2715'}</button>
            )}
          </div>

          <FilterDropdown label="Severity" options={severityOptions} selected={severityFilter} onChange={setSeverityFilter} />
          <FilterDropdown label="Status" options={statusOptions} selected={statusFilter} onChange={setStatusFilter} />
          <FilterDropdown label="Service" options={serviceOptions} selected={serviceFilter} onChange={setServiceFilter} />
        </div>

        {hasActiveFilters && (
          <div className="incidents-filter-row">
            <button className="incidents-clear-all" onClick={clearAllFilters}>Clear filters</button>
          </div>
        )}
      </div>

      {/* Incidents table */}
      <div className="card">
        <div className="incidents-table-toolbar">
          <div className="incidents-table-meta">
            <span className="incidents-table-count">{filteredIncidents.length} of {incidents.length} incidents</span>
            {/* This list refreshes itself every 30 seconds. Saying when it last
                succeeded is the difference between "the data moved" and "the
                page did something inexplicable" — and when a background pass
                fails, this time stops advancing, which is the tell. */}
            <span className="incidents-updated">
              {lastUpdated ? `Updated ${lastUpdated.toLocaleTimeString()}` : 'Not yet updated'}
            </span>
          </div>
          {/* Wrapped, not passed by reference: as the handler, fetchIncidents
              would receive the click event as its isBackground argument, and a
              refresh the user asked for would behave like a silent one. */}
          <button className="btn btn-secondary incidents-refresh-btn" onClick={() => fetchIncidents()}>
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M23 4v6h-6"/><path d="M1 20v-6h6"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/></svg>
            Refresh
          </button>
        </div>
        {paginated.length === 0 ? (
          <p className="empty-state">No incidents found.</p>
        ) : (
          <div className="incidents-table-wrapper">
            <table className="incidents-table resizable-table" style={{ width: tableWidth }}>
              <colgroup>
                {INCIDENTS_COLUMNS.map((c) => (
                  <col key={c.key} style={{ width: `${colWidths[c.key]}px` }} />
                ))}
              </colgroup>
              <thead>
                <tr>
                  {INCIDENTS_COLUMNS.map((c) => (
                    <th
                      key={c.key}
                      className={`resizable-th${c.key === 'escalation' || c.key === 'action' ? '' : ' incidents-th-sortable'}`}
                      onClick={c.key === 'escalation' || c.key === 'action' ? undefined : () => handleSort(c.key)}
                    >
                      {c.label}
                      {c.key !== 'escalation' && c.key !== 'action' && getSortIndicator(c.key)}
                      <span className="col-resize-handle" onMouseDown={handleResizeStart(c.key)} onClick={(e) => e.stopPropagation()} />
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {paginated.map((i) => (
                  <tr key={i.incident_id}>
                    <td className="incidents-ref" title={i.reference_number || ''}>{i.reference_number || '\u2014'}</td>
                    <td>
                      <span className={`incidents-severity incidents-severity--${getSeverityClass(i.severity)}`}>
                        {i.severity || '\u2014'}
                      </span>
                    </td>
                    <td>
                      <span className={`incidents-status incidents-status--${getStatusClass(i.status)}`}>
                        {i.status?.replace(/_/g, ' ') || '\u2014'}
                      </span>
                    </td>
                    <td className="incidents-title-cell">{i.title || i.alarm_name || '\u2014'}</td>
                    <td className="incidents-service">{i.service || '\u2014'}</td>
                    <td className="incidents-assignee">{i.assignee || 'Unassigned'}</td>
                    <td className="incidents-timestamp">{formatTimestamp(i.created_at)}</td>
                    <td>
                      {getEscalationLabel(i.status) ? (
                        <span className="incidents-escalation-badge">{getEscalationLabel(i.status)}</span>
                      ) : (
                        <span className="incidents-escalation-none">—</span>
                      )}
                    </td>
                    <td>
                      <button
                        className="btn-link"
                        onClick={() => navigate(`/incidents/${i.service}/${i.incident_id}`, {
                          state: {
                            page: currentPage,
                            severityFilter, statusFilter, serviceFilter,
                            searchQuery, sortField, sortDirection,
                          },
                        })}
                      >
                        View {'\u2192'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        pageSize={pageSize}
        totalItems={filteredIncidents.length}
        displayedItems={paginated.length}
        onPageChange={setCurrentPage}
        onPageSizeChange={setPageSize}
      />
    </div>
  );
}

export default IncidentsPage;
